import { z } from "zod";

import { ERROR_TYPE } from "@/utils";

const { REQUIRED } = ERROR_TYPE;

// New SignalBase schema for preview responses
export const SignalSourceSchema = z.object({
	url: z.string(),
	sourceType: z.string(),
	title: z.string().nullable(),
	publishedAt: z.string().nullable(),
	isPrimary: z.boolean(),
});

export const SignalInvestorSchema = z.object({
	id: z.string(),
	name: z.string(),
	type: z.string().nullable(),
	website: z.string().nullable(),
	linkedin: z.string().nullable(),
	isLead: z.boolean(),
});

export const FundingSignalSchema = z.object({
	signalId: z.string(),
	signalType: z.string(),
	occurredAt: z.string(),
	discoveredAt: z.string(),
	companyId: z.string(),
	companyName: z.string(),
	companySlug: z.string().nullable(),
	companyLogo: z.string().nullable(),
	companyWebsite: z.string().nullable(),
	companyLinkedin: z.string().nullable(),
	companyIndustry: z.string().nullable(),
	companyCountry: z.string().nullable(),
	companyFoundedYear: z.number().nullable(),
	companyCategories: z.string().nullable(),
	companyDescription: z.string().nullable(),
	companyEmployeeCount: z.number().nullable(),
	roundType: z.string().nullable(),
	roundFlavor: z.string().nullable(),
	amount: z.number().nullable(),
	currency: z.string().nullable(),
	announcedDate: z.string().nullable(),
	confidenceScore: z.number().nullable(),
	verificationStatus: z.string().nullable(),
	isFreeAccess: z.boolean(),
	investors: z.array(SignalInvestorSchema),
	sources: z.array(SignalSourceSchema),
	acquiringCompany: z.object({
		id: z.string(),
		name: z.string(),
		slug: z.string().nullable(),
		logo: z.string().nullable(),
		website: z.string().nullable(),
		linkedin: z.string().nullable(),
		industry: z.string().nullable(),
		country: z.string().nullable(),
		foundedYear: z.number().nullable(),
		employeeCount: z.number().nullable(),
	}),
});

export const FundingSignalResponseSchema = z.object({
	data: z.array(FundingSignalSchema),
	total: z.number(),
});

export type FundingSignal = z.infer<typeof FundingSignalSchema>;
export type FundingSignalResponse = z.infer<typeof FundingSignalResponseSchema>;

// Legacy types for backwards compatibility (can be removed later)
export const ILeadsOnTreesInvestorSchema = z.object({
	name: z.string(),
	linkedin_url: z.string(),
});

export const ILeadsOnTreesCompanyInfoSchema = z.object({
	name: z.string(),
	description: z.string(),
	industry: z.string(),
	website: z.string(),
	size: z.string(),
	logo_url: z.string(),
	year_founded: z.number(),
	hq_country: z.string(),
	email: z.string().nullable(),
	phone: z.string().nullable(),
	keywords: z.array(z.string()),
	specialities: z.array(z.string()),
	private_equity: z.boolean(),
	lbo: z.boolean(),
	smes: z.boolean(),
	investment: z.boolean(),
	sustainability: z.boolean(),
	categories: z.array(z.string()),
	company_interested_in: z.array(z.string()),
});

export const ILeadsOnTreesHistoricalHeadcountSchema = z.object({
	date: z.string(),
	headcount: z.number(),
	growth: z.number(),
});

export const ILeadsOnTreesGrowthInfoSchema = z.object({
	growth_1m: z.number(),
	growth_3m: z.number(),
	growth_6m: z.number(),
	growth_9m: z.number(),
	growth_12m: z.number(),
});

export const ILeadsOnTreesContactEmployeeSchema = z.object({
	name: z.string(),
	email: z.string(),
	phone: z.string().nullable(),
	title: z.string(),
	date_started: z.string(),
	linkedin_url: z.string(),
});

export const ILeadsOnTreesContactInfoSchema = z.object({
	employees: z.array(ILeadsOnTreesContactEmployeeSchema),
	company_linkedin_url: z.string(),
	company_twitter_url: z.string().nullable(),
	company_facebook_url: z.string().nullable(),
});

export const ILeadsOnTreesCompanyFundingSchema = z.object({
	company_name: z.string(),
	acquired_by: z.string().optional(),
	acquired_by_linkedin_url: z.string().optional(),
	acquired_by_company_info: z
		.object({
			name: z.string().optional(),
			website: z.string().optional(),
		})
		.optional(),
	name_path: z.string(),
	round_type: z.string().optional(),
	funding_amount: z.number().optional(),
	acquisition_amount: z.number().optional(),
	acquisition_percentage: z.string().optional(),
	currency: z.string().optional(),
	ceo: z.string().nullable().optional(),
	investors: z.array(ILeadsOnTreesInvestorSchema).optional(),
	company_info: ILeadsOnTreesCompanyInfoSchema,
	historical_headcount: z.array(ILeadsOnTreesHistoricalHeadcountSchema),
	growth_info: ILeadsOnTreesGrowthInfoSchema,
	contact_info: z.array(ILeadsOnTreesContactInfoSchema),
	main_category: z.string(),
	taken_from: z.string().optional(),
	additional_funding_sources: z.array(z.string()).optional(),
	date_seen: z.string(),
});

export const ILeadsOnTreesCompanyFundingResponseSchema = z.object({
	data: z.array(ILeadsOnTreesCompanyFundingSchema),
	total: z.number(),
});

export type ILeadsOnTreesCompanyFunding = z.infer<typeof ILeadsOnTreesCompanyFundingSchema>;
export type ILeadsOnTreesCompanyFundingResponse = z.infer<
	typeof ILeadsOnTreesCompanyFundingResponseSchema
>;
